import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { permissions } from '../../permissions';
import { Page } from '../../model/page';
import { TradingPartnerService } from '../../services/trading-partner.service';
import { Subscription } from 'rxjs/Subscription';
import { Title } from '@angular/platform-browser';
import * as _ from 'lodash';
import { TableExtensionService } from '../oes/table-extension.service';
import { GridFiltersService } from '../../services/grid.filters.service';
import { DisplayFieldPipe } from '../pipes/displayField.pipe';
import { LocalDatePipe } from '../pipes/local-date.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { TradingPartnerModalComponent } from './trading-partner-modal/trading-partner-modal.component';
import { MatDialog } from '@angular/material';
import { NgxPermissionsService } from 'ngx-permissions';
import { AuthService } from 'app/auth/auth.service';

@Component({
  selector: 'app-trading-partners',
  templateUrl: './trading-partners.component.html',
  styleUrls: ['./trading-partners.component.scss'],
})
export class TradingPartnersComponent implements OnInit, OnDestroy {
  @ViewChild('actionbar') actionbar: ElementRef;
  @ViewChild('tableHeader') tableHeader: ElementRef;
  @ViewChild('tableFilter') tableFilter: ElementRef;
  @ViewChild('paginator') paginator: ElementRef;

  private subscription: Subscription;
  public companies: Array<any> = [];
  public selected: Array<any>;
  loading = false;
  public permissions = permissions;
  viewFilters = false;
  private viewportHeight = 0;
  private viewportHeightListener: boolean;

  page = new Page();
  public rows = new Array<any>();
  public cols = [];
  public totalRecords = 0;
  public scrollHeight = '50px';

  public hideTooltip = false;
  public tooltipRows: Array<any> = [];

  public filters: { [key: string]: string } = {};

  hasEditPermission: boolean = false;
  isSuperAdmin: boolean = false;
  canCreateTradingPartner: boolean = false;
  statusTypes: any;

  constructor(
    private tradingPartnerService: TradingPartnerService,
    private toastr: ToastrService,
    private title: Title,
    private tableExtensionService: TableExtensionService,
    private gridFiltersService: GridFiltersService,
    private displayFieldPipe: DisplayFieldPipe,
    private localDatePipe: LocalDatePipe,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private permissionsService: NgxPermissionsService,
    private authService: AuthService
  ) {
    this.page.pageNumber = 0;
    this.page.size = 50;
    this.title.setTitle('EDI - TP Maintenance');
  }

  ngOnInit() {
    this.setCols();
    this.viewFilters = true;
    this.loading = true;
    this.viewportHeightListener = true;
    this.tradingPartnerService.preloadData().subscribe((data) => {
      this.prepareData(data);
      this.setPage({ offset: 0 }, true);
    });

    this.screenHeightListener();

    this.permissionsService
      .hasPermission(this.permissions.EDI_MAINT_EDIT)
      .then((hasPermit) => {
        this.hasEditPermission = hasPermit;
      });

    this.permissionsService
      .hasPermission([this.permissions.ADMIN])
      .then((hasPermit) => {this.isSuperAdmin = hasPermit;
    });

    this.permissionsService
        .hasPermission([this.permissions.EDI_TRADING_PARTNER_ADD])
        .then((hasPermit) => {this.canCreateTradingPartner = hasPermit;
    });
  }

  ngOnDestroy() {
    this.viewportHeightListener = false;

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  prepareData(data) {
    this.companies = data.companies.map((company) => {
      return {
        CO1_ID: company.CO1_ID,
        name: 'CO1_ID:' + company.CO1_ID,
        text: company.CO1_NAME,
      };
    });

    this.statusTypes = [
      { STATUS: 0, text: 'New', name: 'New' },
      { STATUS: 1, text: 'Pending', name: 'Pending' },
      { STATUS: 2, text: 'Approved', name: 'Approved' },
    ];
  }

  setPage(pageInfo, resetTooltips = false) {
    this.page.pageNumber = pageInfo.offset;

    this.subscription = this.serverCall(this.page).subscribe(
      (result) => {
        this.totalRecords = result.count;
        this.rows = result.data;

        if (resetTooltips) {
          this.tooltipRows = [];
        }
        this.populateTooltip();

        this.scrollHeight = this.getScrollHeight() + 'px';
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.loading = false;
        window.dispatchEvent(new Event('resize'));
        this.toastr.error('TimeOut occured', 'Service Error', {
          timeOut: 3000,
        });
      }
    );
  }

  serverCall(page: Page): Observable<any> {
    return this.tradingPartnerService.getTradingPartners(this.page);
  }

  setCols() {
    this.cols = [
      { field: 'ID', header: 'ID', visible: true, width: 50, sort: false },
      {
        field: 'TYPE',
        header: 'Type',
        visible: true,
        width: 50,
        sort: false,
      },
      {
        field: 'CO1_ID',
        header: 'Company',
        visible: true,
        width: 80,
        sort: false,
      },
      {
        field: 'NAME',
        header: 'Name',
        visible: true,
        width: 120,
        sort: false,
      },
      {
        field: 'TP_ID',
        header: 'TP ID',
        visible: true,
        width: 50,
        sort: false,
      },
      {
        field: 'SHOW_DEFAULT',
        header: 'Active',
        visible: true,
        width: 60,
        sort: false,
      },
      {
        field: 'TEST_MODE',
        header: 'Test Mode',
        visible: true,
        width: 60,
        sort: false,
      },
      {
        field: 'STATUS',
        header: 'Status',
        visible: true,
        width: 80,
        sort: false,
      },
      {
        field: 'TICKET_NUMBER',
        header: 'Ticket #',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'RECEIVE_EDI',
        header: 'Send/Receive EDI',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'FILTERS',
        header: 'Connection Method',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'MODIFIED_ON',
        header: 'Modified On',
        visible: true,
        width: 100,
        sort: false,
      },
      { field: 'MAP', header: 'Map', visible: true, width: 80, sort: true },
    ];
  }

  getData() {
    this.loading = true;
    this.page.filterParams = _.clone(this.filters);
    this.setPage({ offset: this.page.pageNumber }, true);
  }

  onPage(event) {
    this.loading = true;
    this.page.size = event.rows;
    this.setPage(
      {
        offset: event.first / event.rows,
      },
      true
    );
  }

  getScrollHeight(): number {
    return (
      this.getContentDialogHeight() -
      (this.tableHeader.nativeElement.offsetHeight +
        (this.paginator.nativeElement.offsetHeight + 55) +
        (this.viewFilters && this.tableFilter
          ? this.tableFilter.nativeElement.offsetHeight
          : 0))
    );
  }

  getContentDialogHeight(): number {
    return (
      window.innerHeight - (this.actionbar.nativeElement.offsetHeight + 48) - 77
    );
  }

  onColumnResize(event) {
    this.tableExtensionService.onColumnResize(this.cols, event);
  }

  onWindowResize() {
    setTimeout(() => {
      this.scrollHeight = this.getScrollHeight() + 'px';
      window.dispatchEvent(new Event('resize'));
    }, 150);
  }

  screenHeightListener() {
    if (!this.viewportHeightListener) {
      return;
    }
    if (window.innerHeight !== this.viewportHeight) {
      this.viewportHeight = window.innerHeight;
      this.onWindowResize();
    }
    setTimeout(() => {
      this.screenHeightListener();
    }, 500);
  }

  toggleFilterView() {
    this.viewFilters = !this.viewFilters;
    this.onWindowResize();
  }

  onSubmit() {
    const filters = this.gridFiltersService.prepare(this.filters);
    let reload = false;
    const keys = Object.keys(filters).concat(
      Object.keys(this.page.filterParams)
    );
    for (const key of keys) {
      if (this.page.filterParams[key] !== filters[key]) {
        reload = true;
        break;
      }
    }

    if (reload) {
      this.page.filterParams = filters;
      this.getData();
    }
  }

  clearFilters() {
    if (this.viewFilters) {
      this.page.filterParams = {};
      this.filters = {};
      this.page.pageNumber = 0;
      this.loading = true;
      this.setPage({ offset: this.page.pageNumber }, true);
    }
  }

  onHover(e, index = null) {
    const aux = e.target.offsetWidth < e.target.scrollWidth;
    this.hideTooltip = !aux;
  }

  populateTooltip() {
    this.rows.forEach((element) => {
      const newObj = JSON.parse(JSON.stringify(element));

      newObj['CO1_ID'] = this.displayFieldPipe.transform(
        newObj['CO1_ID'],
        'CO1_ID',
        'text',
        this.companies
      );

      newObj['MODIFIED_ON'] = newObj['MODIFIED_ON']
        ? this.localDatePipe.transform(
            newObj['MODIFIED_ON'],
            'MMM dd, yyyy hh:mm:ss a'
          )
        : '-';

      newObj['SHOW_DEFAULT'] = newObj['SHOW_DEFAULT'] === 'X' ? 'YES' : 'NO';
      newObj['TEST_MODE'] = newObj['TEST_MODE'] === '1' ? 'YES' : 'NO';

      newObj['STATUS'] = this.displayFieldPipe.transform(
        newObj['STATUS'],
        'STATUS',
        'text',
        this.statusTypes
      );

      newObj['RECEIVE_EDI'] =
        newObj['RECEIVE_EDI'] === '1' ? 'Receive EDI -' : '';
      newObj['RECEIVE_EDI'] +=
        newObj['SEND_EDI_PO'] === '1' ? 'Send EDI PO -' : '';
      newObj['RECEIVE_EDI'] +=
        newObj['SEND_EDI_INVOICES'] === '1' ? 'Send EDI Invoices ' : '';

      newObj['FILTERS'] = newObj['SEND_FTP'] === '1' ? 'Send via FTP -' : '';
      newObj['FILTERS'] += newObj['SEND_SFTP'] === '1' ? 'Send via SFTP -' : '';
      newObj['FILTERS'] += newObj['POST_AS2'] === '1' ? 'Send via AS2 ' : '';

      this.tooltipRows.push(newObj);
    }, this);
    return this.tooltipRows;
  }

  openTypeModal() {
    const dialogRef = this.dialog.open(TradingPartnerModalComponent, {
      width: '400px',
      height: '260px',
      role: 'dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result === 'Customer') {
          this.addCustomer();
        } else {
          this.addVendor();
        }
      }
    });
  }

  addCustomer(): void {
    this.router.navigate(['trading-partners/add-customer/0/' + this.authService.companyId]);
  }

  addVendor(): void {
    this.router.navigate(['trading-partners/add-vendor/0/' + this.authService.companyId]);
  }

  cellDoubleClick(record) {
    if (record.TYPE === 'Customer') {
      this.accessCustomer(record);
    } else {
      this.accessVendor(record);
    }
  }

  accessVendor(record): void {
    this.router.navigate([
      'trading-partners/edit-vendor/' + record.ID + '/' + record.CO1_ID,
    ]);
  }

  accessCustomer(record): void {
    this.router.navigate([
      'trading-partners/edit-customer/' + record.ID + '/' + record.CO1_ID,
    ]);
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/trading-partners/trading-partners.component.ts